import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Modal, Field, CURRENCIES, Segmented } from "./ui";
import { api } from "../lib/api";
import useGuardedSubmit from "../lib/useGuardedSubmit";

const today = () => new Date().toISOString().slice(0, 10);

const EMPTY = {
  provider: "", product_name: "", fund_type: "mixed", currency: "EUR",
  contribution_amount: "", contribution_frequency: "monthly", annual_increase_pct: "2",
  start_date: today(), term_years: "40", upfront_fee_pct: "2.5", upfront_fee_years: "5",
  ongoing_fee_pct: "1", expected_return_pct: "6", current_value_override: "", notes: "",
};

const FUND_TYPES = [
  { value: "equity", key: "fundTypeEquity" },
  { value: "mixed", key: "fundTypeMixed" },
  { value: "real_estate", key: "fundTypeRealEstate" },
  { value: "other", key: "fundTypeOther" },
];

export default function InsurancePolicyDialog({ open, onClose, policy, onDone }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);
  const [saving, guardedSubmit] = useGuardedSubmit();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    if (policy) {
      setForm({
        provider: policy.provider, product_name: policy.product_name, fund_type: policy.fund_type,
        currency: policy.currency, contribution_amount: policy.contribution_amount,
        contribution_frequency: policy.contribution_frequency, annual_increase_pct: policy.annual_increase_pct,
        start_date: policy.start_date?.slice(0, 10), term_years: policy.term_years,
        upfront_fee_pct: policy.upfront_fee_pct, upfront_fee_years: policy.upfront_fee_years,
        ongoing_fee_pct: policy.ongoing_fee_pct, expected_return_pct: policy.expected_return_pct,
        current_value_override: policy.current_value_override ?? "", notes: policy.notes || "",
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, policy]);

  const submit = () => {
    if (!form.provider || !form.product_name || !form.contribution_amount || !form.start_date) {
      toast.error(t("dialogs.failed")); return;
    }
    const payload = {
      ...form,
      contribution_amount: parseFloat(form.contribution_amount) || 0,
      annual_increase_pct: parseFloat(form.annual_increase_pct) || 0,
      term_years: parseInt(form.term_years, 10) || 40,
      upfront_fee_pct: parseFloat(form.upfront_fee_pct) || 0,
      upfront_fee_years: parseInt(form.upfront_fee_years, 10) || 5,
      ongoing_fee_pct: parseFloat(form.ongoing_fee_pct) || 0,
      expected_return_pct: parseFloat(form.expected_return_pct) || 0,
      current_value_override: form.current_value_override === "" ? null : parseFloat(form.current_value_override),
    };
    guardedSubmit(async () => {
      try {
        if (policy) await api.updateInsurancePolicy(policy.id, payload);
        else await api.createInsurancePolicy(payload);
        toast.success(t("insurance.save"));
        onDone && onDone(); onClose();
      } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
    });
  };

  return (
    <Modal open={open} onClose={onClose} title={policy ? t("insurance.editPolicy") : t("insurance.addPolicy")} wide testid="insurance-policy-dialog">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label={t("insurance.provider")}>
            <input data-testid="policy-provider-input" className="input" value={form.provider} onChange={(e) => set("provider", e.target.value)} placeholder="ERGO" />
          </Field>
          <Field label={t("insurance.productName")}>
            <input data-testid="policy-product-input" className="input" value={form.product_name} onChange={(e) => set("product_name", e.target.value)} placeholder="ERGO Renten Chance" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label={t("insurance.fundType")}>
            <select data-testid="policy-fundtype-select" className="input" value={form.fund_type} onChange={(e) => set("fund_type", e.target.value)}>
              {FUND_TYPES.map((ft) => <option key={ft.value} value={ft.value}>{t(`insurance.${ft.key}`)}</option>)}
            </select>
          </Field>
          <Field label={t("dialogs.currency")}>
            <select data-testid="policy-currency-select" className="input" value={form.currency} onChange={(e) => set("currency", e.target.value)}>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label={t("insurance.contributionAmount")}>
            <input data-testid="policy-amount-input" type="number" className="input tabular" value={form.contribution_amount} onChange={(e) => set("contribution_amount", e.target.value)} placeholder="200" />
          </Field>
          <div>
            <span className="label block mb-1.5">{t("insurance.contributionFrequency")}</span>
            <Segmented options={[{ value: "monthly", label: t("insurance.monthly") }, { value: "yearly", label: t("insurance.yearly") }]}
              value={form.contribution_frequency} onChange={(v) => set("contribution_frequency", v)} testid="policy-frequency" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label={t("insurance.startDate")}>
            <input data-testid="policy-start-date-input" type="date" className="input" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} />
          </Field>
          <Field label={t("insurance.termYears")}>
            <input data-testid="policy-term-input" type="number" className="input tabular" value={form.term_years} onChange={(e) => set("term_years", e.target.value)} />
          </Field>
        </div>

        <Field label={t("insurance.annualIncrease")}>
          <input data-testid="policy-increase-input" type="number" step="0.1" className="input tabular" value={form.annual_increase_pct} onChange={(e) => set("annual_increase_pct", e.target.value)} />
        </Field>

        <div className="pt-2 border-t border-slate-100">
          <span className="label block mb-3 mt-3">{t("insurance.upfrontFeePct")} / {t("insurance.ongoingFeePct")}</span>
          <div className="grid grid-cols-3 gap-4">
            <Field label={t("insurance.upfrontFeePct")}>
              <input data-testid="policy-upfront-fee-input" type="number" step="0.1" className="input tabular" value={form.upfront_fee_pct} onChange={(e) => set("upfront_fee_pct", e.target.value)} />
            </Field>
            <Field label={t("insurance.upfrontFeeYears")}>
              <input data-testid="policy-upfront-years-input" type="number" className="input tabular" value={form.upfront_fee_years} onChange={(e) => set("upfront_fee_years", e.target.value)} />
            </Field>
            <Field label={t("insurance.ongoingFeePct")}>
              <input data-testid="policy-ongoing-fee-input" type="number" step="0.1" className="input tabular" value={form.ongoing_fee_pct} onChange={(e) => set("ongoing_fee_pct", e.target.value)} />
            </Field>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label={t("insurance.expectedReturn")}>
            <input data-testid="policy-return-input" type="number" step="0.1" className="input tabular" value={form.expected_return_pct} onChange={(e) => set("expected_return_pct", e.target.value)} />
          </Field>
          <Field label={t("insurance.currentValueOverride")}>
            <input data-testid="policy-value-override-input" type="number" className="input tabular" value={form.current_value_override} onChange={(e) => set("current_value_override", e.target.value)} placeholder="—" />
          </Field>
        </div>

        <Field label={t("insurance.notes")}>
          <textarea data-testid="policy-notes-input" className="input min-h-[60px] resize-none" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        </Field>
      </div>

      <div className="mt-6 flex flex-wrap justify-end gap-3">
        <button className="btn-ghost" onClick={onClose}>{t("insurance.cancel")}</button>
        <button data-testid="policy-submit-btn" className="btn-primary" onClick={submit} disabled={saving}>
          {saving ? <Loader2 size={16} className="animate-spin" /> : null} {t("insurance.save")}
        </button>
      </div>
    </Modal>
  );
}
